import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Plus, X, Search, Pencil, Trash2 } from "lucide-react";

const STATUSES = ["Hazırlanıyor", "Yolda", "Teslim Edildi", "İptal"];
const COLORS = { "Hazırlanıyor": "#B8933F", "Yolda": "#B8933F", "Teslim Edildi": "#2F6E4E", "İptal": "#A63A3A" };
const FIELDS = [
  { k: "musteri", l: "Müşteri", req: true }, { k: "teslimat_adresi", l: "Teslimat Adresi", req: true, textarea: true },
  { k: "urun", l: "Ürün / Model" }, { k: "telefon", l: "Telefon" }, { k: "aciklama", l: "Açıklama" },
];

export default function Shipments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sehir, setSehir] = useState("");
  const [durum, setDurum] = useState("");
  const [form, setForm] = useState(null); // {} for new, record for edit
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q; if (sehir) params.adres = sehir; if (durum) params.durum = durum;
      const { data } = await api.get("/shipments", { params });
      setRows(data);
    } catch (e) { toast.error("Sevkiyatlar yüklenemedi"); } finally { setLoading(false); }
  }, [q, sehir, durum]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/shipments/${id}`, payload);
      else await api.post("/shipments", payload);
      toast.success("Kaydedildi"); setForm(null); load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const changeStatus = async (r, d) => {
    try { await api.post(`/shipments/${r.id}/status`, { durum: d }); load(); } catch (e) { toast.error("Güncellenemedi"); }
  };

  const remove = (id) => {
    setConfirm({
      title: "Sevkiyatı Sil",
      description: "Bu sevkiyatı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      onConfirm: async () => {
        try { await api.delete(`/shipments/${id}`); toast.success("Silindi"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
        finally { setConfirm(null); }
      },
    });
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">TESLİMAT PLANI</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sevkiyat Planı</h1>
        </div>
        <button onClick={() => setForm({})} data-testid="add-shipment-button" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
          <Plus className="w-4 h-4" /> Yeni Sevkiyat
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div className="flex-1 min-w-[160px]">
          <label className="kicker block mb-1.5">MÜŞTERİ ARA</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8377]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ship-search" placeholder="Müşteri..." className="w-full pl-9 pr-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="kicker block mb-1.5">ADRES</label>
          <input value={sehir} onChange={(e) => setSehir(e.target.value)} data-testid="ship-adres-filter" placeholder="Adres / il..." className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
        </div>
        <div className="w-[170px]">
          <label className="kicker block mb-1.5">DURUM</label>
          <select value={durum} onChange={(e) => setDurum(e.target.value)} data-testid="ship-durum-filter" className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]">
            <option value="">Tümü</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-16rem)]" data-testid="shipments-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              {["Müşteri", "Teslimat Adresi", "Ürün", "Telefon", "Sevk. Tarihi", "Durum", "İşlem"].map((h, i) => (
                <th key={h} className={`px-3 py-2.5 kicker ${i === 6 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]" data-testid="ship-empty">Sevkiyat yok.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] transition-colors duration-150 ${i % 2 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`ship-row-${r.id}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{r.musteri}</td>
                <td className="px-3 py-2 max-w-[280px]">{r.teslimat_adresi || r.sehir || <span className="text-[#B8933F]">Adres girin</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.urun || "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.telefon || "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDate(r.sevkiyat_tarihi)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <select value={r.durum} onChange={(e) => changeStatus(r, e.target.value)} data-testid={`ship-status-${r.id}`} className="px-2 py-1 rounded-sm text-xs font-semibold border-0 cursor-pointer" style={{ backgroundColor: `${COLORS[r.durum]}1a`, color: COLORS[r.durum] }}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="flex justify-end gap-1">
                    {isAdmin ? (
                      <>
                        <button onClick={() => setForm(r)} data-testid={`ship-edit-${r.id}`} className="p-1.5 text-[#8C8377] hover:text-[#B8933F] hover:bg-white rounded-sm transition-colors duration-150"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(r.id)} data-testid={`ship-delete-${r.id}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] hover:bg-white rounded-sm transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                      </>
                    ) : <span className="text-xs text-[#8C8377]">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && <ShipmentForm initial={form.id ? form : null} onClose={() => setForm(null)} onSubmit={(p) => save(p, form.id)} />}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
    </Layout>
  );
}

function ShipmentForm({ initial, onClose, onSubmit }) {
  const [f, setF] = useState({ musteri: "", teslimat_adresi: "", urun: "", telefon: "", aciklama: "", sevkiyat_tarihi: "", durum: "Hazırlanıyor" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (initial) setF({ ...initial, teslimat_adresi: initial.teslimat_adresi || initial.sehir || "", sevkiyat_tarihi: (initial.sevkiyat_tarihi || "").slice(0, 10) }); }, [initial]);
  const ch = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.musteri || !f.teslimat_adresi) { toast.error("Müşteri ve Teslimat Adresi zorunlu"); return; }
    setSaving(true);
    try { await onSubmit({ musteri: f.musteri, teslimat_adresi: f.teslimat_adresi, sehir: "", urun: f.urun, telefon: f.telefon, aciklama: f.aciklama, sevkiyat_tarihi: f.sevkiyat_tarihi || null, durum: f.durum }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-lg my-16" data-testid="shipment-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">{initial ? "Sevkiyatı Düzenle" : "Yeni Sevkiyat"}</h3>
          <button onClick={onClose} data-testid="ship-form-close" aria-label="Kapat" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map((fl) => (
              <div key={fl.k} className={(fl.k === "aciklama" || fl.textarea) ? "col-span-2" : ""}>
                <label className="kicker block mb-1.5">{fl.l}{fl.req ? " *" : ""}</label>
                {fl.textarea ? (
                  <textarea value={f[fl.k] || ""} onChange={(e) => ch(fl.k, e.target.value)} required={fl.req} rows={2} data-testid={`ship-field-${fl.k}`} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" placeholder="Mah. Cad. No:.. Daire:.. İlçe/İl" />
                ) : (
                  <input value={f[fl.k] || ""} onChange={(e) => ch(fl.k, e.target.value)} required={fl.req} data-testid={`ship-field-${fl.k}`} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
                )}
              </div>
            ))}
            <div>
              <label className="kicker block mb-1.5">SEVKİYAT TARİHİ</label>
              <DatePicker value={f.sevkiyat_tarihi} onChange={(v) => ch("sevkiyat_tarihi", v)} placeholder="Tarih seçin" testId="ship-field-tarih" />
            </div>
            <div>
              <label className="kicker block mb-1.5">DURUM</label>
              <select value={f.durum} onChange={(e) => ch("durum", e.target.value)} data-testid="ship-field-durum" className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">İptal</button>
            <button type="submit" disabled={saving} data-testid="ship-form-save" className="px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
